import type {
  AssessmentCategory,
  AssessmentConfidence,
  AssessmentDimension,
  AssessmentEffort,
  AssessmentSafetyClass,
  AssessmentSeverity,
  AssessmentSignalStrength,
  SiteAgainAssessmentReportV1,
} from "@siteagain/shared-types";

import type { Locale } from "./i18n";

type Qualification = SiteAgainAssessmentReportV1["qualification"];
type DimensionKey = keyof SiteAgainAssessmentReportV1["dimensions"];

/**
 * Interface copy for the assessment surfaces: the pre-signup report, the
 * studio's audit tab, and the sign-in step between them. Report content itself
 * (finding titles, observations) is written by the pipeline in the locale the
 * audit was requested in; nothing here paraphrases or replaces it.
 */
const en = {
  languageLabel: "Language",
  signIn: "Sign in",

  kicker: "Website check",
  heroLead: "What we found on",
  heroLede: "What buyers, Google and the AI assistants see today — measured, not guessed.",
  source: "Site checked",
  method: "Method",
  methodSebora: "Full check",
  methodLocal: "Homepage-only check",
  captured: "Captured",
  validUntil: "Valid until",
  partial: "A few checks could not run.",
  partialLink: "See which ones",
  reportLanguage: (language: string) => `The findings were written in ${language}; the interface follows your language choice.`,
  languageName: { en: "English", fr: "French" } as Record<string, string>,

  nav: { verdict: "The answer", buyers: "What buyers see", foundation: "Site detail", opportunities: "What to fix", evidence: "Proof", action: "Plan", pilot: "Next step", limits: "Limits" },
  reportSections: "Report sections",

  verdictLabel: "The short answer",
  verdict: {
    supported: { headline: "Your site is ready to be rebuilt better.", detail: "The evidence is strong enough for us to build a private proof of your homepage." },
    review_required: { headline: "Almost ready — one quick review first.", detail: "We can go ahead, but a person should check the points below before the proof is built." },
    unsupported: { headline: "We cannot rebuild this one automatically yet.", detail: "Something prevents a reliable automated rebuild. The reasons are listed below." },
  } satisfies Record<Qualification["status"], { headline: string; detail: string }>,
  whyLabel: "Why",
  riskLabel: "Rebuild risk",
  risk: { low: "Low", medium: "Medium", high: "High" } satisfies Record<Qualification["risk"], string>,
  soWhat: {
    title: "What this means",
    topOpportunity: "Top opportunity",
    noRedesignLabel: "No redesign",
    noRedesign: (fixable: number, total: number) => `${fixable} of ${total} finding${total === 1 ? "" : "s"} can be tried in a private preview — no redesign required.`,
    noRedesignNone: "No finding here calls for a redesign.",
  },

  // Buyer-facing evidence: the searches buyers run, the assistants they ask,
  // how the homepage loads, and the page basics. All plain language.
  buyers: {
    title: "What buyers see today",
    lede: "We ran the searches your buyers run, asked the assistants they ask, loaded your homepage in a real browser, and checked the pages we sampled.",
    none: "No buyer checks were recorded for this report.",
    rankTitle: "Where you rank on Google",
    rankLede: "The exact searches we ran, and where your site appeared.",
    rankPosition: (position: number) => `#${position} on Google`,
    rankNotTop: (limit: number) => `Not in the top ${limit} we captured`,
    rankResultsTitle: "Who gets the click",
    rankYou: "You",
    rankUnavailable: "We could not read search results for this report.",
    aiTitle: "Do the AIs recommend you?",
    aiLede: "We asked each assistant the questions buyers ask when they choose a provider, and recorded every answer word for word.",
    aiMentioned: "Mentions you",
    aiNotMentioned: "Does not mention you",
    aiRecommended: "Recommends you",
    aiNotRecommended: "Does not recommend you",
    aiNamedLabel: "Also named",
    aiUnavailable: "No AI assistant answered for this report.",
    speedTitle: "How fast your homepage loads",
    speedLede: "Measured once in a real browser — not a lab score.",
    speedSeconds: (seconds: number) => `${seconds}s`,
    speedGood: "Fast",
    speedOk: "Acceptable",
    speedSlow: "Slow",
    speedDetail: (ttfbMs: number, requests: number) => `First response in ${ttfbMs} ms · ${requests} requests`,
    speedUnavailable: "Speed was not measured for this report.",
    healthTitle: "Website health",
    healthLede: "The basics buyers and search engines expect, on the pages we checked.",
    healthPages: (checked: number) => `${checked} page${checked === 1 ? "" : "s"} checked`,
    healthClean: "Nothing missing on the pages we checked: titles, descriptions, H1 headings and image alt text are all in place.",
    healthIssuesUnit: (affected: number, checked: number) => `${affected} of ${checked}`,
    healthFixable: "All of this is fixable without a redesign.",
    healthIssue: {
      missing_meta_description: "pages with no meta description",
      missing_h1: "pages with no H1 heading",
      missing_title: "pages with no title",
      images_without_alt: "images with no alt text",
    } as Readonly<Record<string, string>>,
    unavailableNote: "Some checks could not run:",
    unavailableReason: {
      search_provider_unconfigured: "no search provider is connected",
      search_query_failed: "a search query failed",
      search_queries_unavailable: "buyer searches could not be prepared",
      ai_models_unconfigured: "no AI assistants are connected",
      ai_visibility_unavailable: "no AI assistant answered",
      ai_assistant_unavailable: "an AI assistant did not answer",
      ai_prompts_unavailable: "buyer questions could not be prepared",
      page_sample_unavailable: "no page sample was captured",
      page_content_unavailable: "we could not read this page's content",
      speed_measurement_unconfigured: "no browser is connected for speed",
      speed_measurement_unavailable: "the speed measurement produced no timing",
      speed_measurement_failed: "the speed measurement failed",
    } as Readonly<Record<string, string>>,
    footnote: "Searches and AI answers were captured for this report; both change over time.",
  },

  foundationTitle: "The technical detail",
  foundationLede: "The engineer's view of your site. Skip this section if you only care about what buyers see.",
  bands: "Bands: 0–44 weak · 45–74 room to improve · 75–100 solid",
  scoresTitle: "The scores",
  scoresLede: "Four measurements: is the site findable, quotable by an assistant, fast, and safe to change.",
  platformLabel: "How this site is built",
  platformDetected: (label: string) => `Detected: ${label}`,
  platformContentInHtml: "The content arrives in the HTML.",
  platformContentInJs: "The content is added by JavaScript after the page loads.",
  dimensions: {
    seo: { label: "Search", question: "Can search engines find and understand this page?" },
    geo: { label: "AI answers", question: "Can an AI assistant quote this page accurately?" },
    performance: { label: "Speed", question: "How quickly does the page become usable on mobile?" },
    maintainability: { label: "Easy to update", question: "How consistently is this site structured for future changes?" },
  } satisfies Record<DimensionKey, { label: string; question: string }>,
  publicEstimate: "Public-site estimate",
  verifiedInPilot: "Verified during Pilot",
  weakestSignals: "Signals behind the score",
  signalsToggle: "What is behind this score",
  componentLabels: {
    template_consistency: "Template consistency",
    frontend_complexity: "Frontend complexity",
    markup_consistency: "Markup consistency",
    architecture: "Site architecture",
    content_structure: "Content structure",
    dependency_burden: "Dependency burden",
    change_risk: "Change risk",
    delivery_model: "How the content is delivered",
  } as Readonly<Record<string, string>>,
  componentBasis: {
    template_consistency: (i: Record<string, number>) =>
      `Average structural similarity across ${i.pairs ?? 0} comparable page pair${(i.pairs ?? 0) === 1 ? "" : "s"}.`,
    frontend_complexity: (i: Record<string, number>) =>
      `${i.scripts ?? 0} scripts (${i.thirdPartyScripts ?? 0} third-party), DOM depth ${i.domDepth ?? 0}, ${i.domNodes ?? 0} nodes.`,
    markup_consistency: (i: Record<string, number>) =>
      `${i.inlineStyles ?? 0} inline styles, ${i.styleTags ?? 0} style blocks, depth spread ${i.depthSpread ?? 0}.`,
    architecture: (i: Record<string, number>) =>
      `${i.minInternalLinks ?? 0} internal links on the sparsest sampled page, canonical on ${i.canonicalPercent ?? 0}% of pages.`,
    content_structure: (i: Record<string, number>) =>
      `${i.present ?? 0} of ${i.checks ?? 0} metadata patterns present across the sampled pages.`,
    dependency_burden: (i: Record<string, number>) =>
      `${i.thirdPartyDomains ?? 0} third-party domains, ${i.thirdPartyScripts ?? 0} third-party scripts.`,
    change_risk: (i: Record<string, number>) =>
      `Canonical ${i.canonicalPercent ?? 0}% · description ${i.descriptionPercent ?? 0}% · structured data ${i.schemaPercent ?? 0}% present.`,
    delivery_model: (i: Record<string, number>) => {
      switch (i.deliveryClass) {
        case 3: return "Finished HTML: anything that reads the page sees the content without running scripts.";
        case 2: return "Assembled per request by the platform, so changes depend on its templates, plugins and plan.";
        case 1: return "Prerendered HTML, but the page is still assembled by the application in the browser.";
        default: return "The content is added by JavaScript, so a reader that does not run scripts gets an empty page.";
      }
    },
  } as Readonly<Record<string, (inputs: Record<string, number>) => string>>,
  status: {
    strong: "Solid",
    opportunity: "Room to improve",
    limited: "Weak",
    not_assessed: "Not assessed",
  } satisfies Record<AssessmentDimension["status"], string>,
  notScored: "No score",
  confidence: "Confidence",
  confidenceLevel: { high: "high", medium: "medium", low: "low", insufficient: "insufficient" } satisfies Record<AssessmentConfidence, string>,

  opportunitiesTitle: "What to fix",
  opportunitiesCount: (count: number) => count === 0 ? "Nothing to fix" : `${count} fix${count === 1 ? "" : "es"}`,
  opportunitiesLede: "Open a fix to see why it matters and what we would change.",
  opportunitiesEmpty: "This check found nothing to fix. That describes what we could check, not proof the site has no issues — the limits section says what was out of scope.",
  filterAll: "All",
  filterEmpty: "Nothing at this level.",
  severity: { critical: "Critical", warning: "Warning", opportunity: "Opportunity" } satisfies Record<AssessmentSeverity, string>,
  severityPlural: { critical: "Critical", warning: "Warnings", opportunity: "Opportunities" } satisfies Record<AssessmentSeverity, string>,
  severityHint: {
    critical: "Fix first",
    warning: "Holds the page back",
    opportunity: "Worth doing",
  } satisfies Record<AssessmentSeverity, string>,
  category: {
    qualification: "Eligibility",
    seo: "Search",
    geo: "AI answers",
    performance: "Speed",
    maintainability: "Easy to update",
  } satisfies Record<AssessmentCategory, string>,
  observed: "What we saw",
  consequence: "Why it matters",
  remediation: "What we would change",
  technicalDetail: "Technical detail",
  directlyObserved: "Directly observed",
  likely: "Likely, not directly observed",
  pages: "Pages",
  morePages: (count: number) => `+${count} more`,
  evidence: (count: number) => `Evidence (${count})`,
  evidenceKind: {
    provider_observation: "provider observation",
    page_fact: "page fact",
    performance_metric: "performance metric",
  } as Record<string, string>,
  safety: {
    safe_preview: "Can be tried in a private preview",
    observe: "Observation only",
    expert_review: "Needs expert review",
  } satisfies Record<AssessmentSafetyClass, string>,
  proposeFix: "Propose this change",
  expand: "Show details",
  collapse: "Hide details",
  priority: { critical: "Do first", warning: "Do next", opportunity: "Worth testing" } satisfies Record<AssessmentSeverity, string>,
  signalLabel: "Expected signal",
  signalLevel: { high: "High", medium: "Medium", low: "Low" } satisfies Record<AssessmentSignalStrength, string>,
  effortLabel: "Effort",
  effortLevel: { low: "Low", medium: "Medium", high: "High" } satisfies Record<AssessmentEffort, string>,

  evidenceTitle: "Proof",
  evidenceLede: "Where each fix comes from. Open an item in place when a fix cites it.",
  evidenceCount: (count: number) => count === 0 ? "No evidence recorded" : `${count} evidence item${count === 1 ? "" : "s"}`,
  evidenceEmpty: "This report cites no evidence items. That describes what the scan recorded, not an absence of issues.",

  actionTitle: "What happens next",
  actionLede: "What a first proof would focus on, and how each change gets checked.",
  recommendedPage: "Page with the most to gain",
  homepageCaveat: "The first proof still rebuilds your homepage, because it becomes the new site's entry page. This page is where the full migration would pay off most.",
  proofPlanTitle: "What the proof will demonstrate",
  checkedBy: "How we check it",
  materialTitle: "What we changed, and why",
  changeType: {
    fidelity: "Fidelity",
    seo_optimization: "Search",
    geo_enhancement: "AI answers",
    performance_gain: "Speed",
    maintainability: "Maintainability",
  } as Record<string, string>,

  limitsTitle: "Scope and limits",
  limitsDisclaimer: "This assessment describes what we observed at capture time. It does not predict rankings, traffic, or whether an AI assistant will cite your site.",

  // Pre-signup page
  scanning: (host: string) => `Scanning ${host}: search structure, AI readability, mobile speed and migration fit.`,
  scanningDetail: "This usually takes under a minute. Your live site is not touched.",
  scanSteps: ["Search signals", "AI readability", "Mobile speed", "Migration fit"],
  scanFailed: (host: string) => `We could not finish scanning ${host}.`,
  scanFailedGeneric: "We could not finish scanning that address.",
  scanFailedDetail: "The address may be unreachable, blocked to crawlers, or not a public page.",
  tryAnother: "Try a different address",
  ctaTitle: "Want your homepage rebuilt on this evidence?",
  ctaOpen: (host: string) => `An account starts a private proof from this assessment. We will not scan ${host} again, and nothing is published without your approval.`,
  ctaInvite: "Building the proof is invite-only while SiteAgain is in private testing. This assessment stays yours either way.",
  ctaRegister: "Register my interest",
  ctaRegistered: "Noted. We open access in order of interest.",
  ctaCreate: "Create an account and build my homepage",
  ctaInviteSignIn: "I have an invite — sign in",

  // Email verification gate (the full report is displayed only after the
  // emailed link is opened — see docs/20-assessment-contract.md)
  emailGate: {
    badge: "Full Audit Report",
    unlockTitle: "Unlock your full assessment",
    unlockSubtitle: (host: string) => `Your assessment for ${host} is ready. Enter your work email and we'll send you a private link. The full report unlocks once your email is confirmed.`,
    emailLabel: "Work email address",
    emailPlaceholder: "alex@company.com",
    nameLabel: "Your name (optional)",
    namePlaceholder: "Alex Dupont",
    unlockButton: "Email me the private link",
    submitting: "Sending…",
    sentTitle: "Check your inbox",
    sentBody: (email: string, host: string) => `We're analysing ${host}. We've sent a confirmation link to ${email}. Your full report will unlock once your email is confirmed.`,
    resendButton: "Send the link again",
    resending: "Sending…",
    resendDone: "Link sent again. Check your inbox.",
    lockedNotice: "The detailed findings, the evidence behind them, and the action plan stay locked until your email is confirmed. No account is needed.",
    consentText: "I agree to receive my assessment report and related communications from SiteAgain.",
    scanPrompt: "We'll send your private assessment to this email when it's ready:",
    scanButton: "Email me the assessment",
    scanSaved: (email: string) => `We'll send your assessment to ${email} once the scan finishes. Confirm the link in that email to unlock the full report.`,
    verified: "Email confirmed. Your report is unlocked.",
  },

  // Radical transparency for top performers (all assessed dimension scores > 90)
  highScores: {
    badge: "Top Technical Performance (Scores > 90)",
    headline: "Your technical foundations are already excellent",
    detail: "Radical transparency: you are already doing a great job on technical elements! Your site demonstrates clean structure, fast mobile response, and solid search foundations. You do not need a disruptive technical rebuild or emergency migration. Instead, the real opportunity is keeping this momentum and going further to grow: protecting your high scores against regressions, and implementing weekly search opportunities.",
    growthHighlight: "How we help you go further: continuous monitoring to maintain your scores + weekly recommendations based on real Google Search Console queries and search volume data.",
  },

  // €59 / month Growth Monitor plan
  growthMonitor: {
    badge: "Growth Monitor",
    price: "€59 / month",
    priceNote: "per site · cancel anytime",
    headline: "Your technical foundation + a weekly growth loop",
    summary: "Continuous technical monitoring of your site, a short list of prioritised recommendations every week, and 7 / 14 / 28-day measurement of what changed.",
    baseTitle: "Continuous technical monitoring",
    baseDetail: "We keep watching speed, indexing and search structure so the foundations stay stable between changes.",
    recsTitle: "Weekly prioritised recommendations",
    recsDetail: "Each week, the opportunities that matter most, ordered by the search and technical evidence behind them — ready for you to decide on.",
    implementsNote: "SiteAgain identifies, prioritises and measures the work. You decide which recommendations you implement.",
    cta: "Start growth monitoring",
  },

  pilotTitle: "Pilot",
  pilotLede: "Whether a pilot fits this site, and what continuous growth would look like.",
  pilotUnavailable: {
    badge: "Pilot",
    headline: "Pilot eligibility was not recorded for this report.",
    detail: "This report was produced before pilot classification existed, so there is nothing here to say whether a pilot would fit. We do not guess in its place.",
  },
  pilotStrong: {
    badge: "Pilot — strong fit",
    headline: "This site is a good candidate for a pilot.",
    detail: "The findings above are concrete and measurable, which is what a pilot needs: a before, a change, and an after we can compare.",
  },
  pilotApplyFallback: "Pilot places are limited while SiteAgain is in private testing. We will follow up using the email this report was sent to.",
  pilotApply: {
    title: "Apply to Pilot",
    lede: "Three quick questions. We review applications manually because we only accept sites where we believe we can create measurable value.",
    horizonLabel: "What matters most over the next 3 months?",
    horizonPlaceholder: "More qualified leads from the service pages",
    managerLabel: "Who manages the website today?",
    managerOptions: { me: "Me", developer: "A developer", agency: "An agency", nobody: "Nobody currently" },
    gscLabel: "Can you give SiteAgain read-only Search Console access?",
    gscOptions: { yes: "Yes", no: "Not yet" },
    capacityLabel: "Can you implement roughly 1–3 recommendations a week?",
    capacityHint: "Most recommendations are content or metadata edits, not code.",
    capacityOptions: { yes: "Yes", no: "No" },
    submit: "Submit Pilot application",
    submitting: "Submitting…",
    doneTitle: "Application received",
    doneBody: (email: string) => `We review Pilot applications manually. We'll come back to you at ${email} with a decision, and if we accept, Pilot starts by connecting Search Console to establish your baseline.`,
    error: "The application could not be submitted. Please try again.",
  },
  pilotFoundation: {
    badge: "Pilot — grow instead",
    headline: "This site does not need fixing. It needs growing.",
    detail: "The foundations are solid and no finding calls for a rebuild, so a migration pilot has nothing to prove here. The useful next step is the growth loop below: monitor the technical base and act on the search opportunities that appear each week.",
  },
  pilotInsufficient: {
    badge: "Pilot — not measurable yet",
    headline: "There is not enough evidence yet to measure a pilot.",
    detail: "A pilot only makes sense when we can compare a before and an after. This report does not carry enough observable evidence for that, and we would rather say so than promise a measurement we cannot make.",
  },
  weeklySample: {
    badge: "Example",
    title: "What you would receive next week",
    item: {
      title: "A query you already appear for, but not on page one",
      body: "When search data is connected, we look for pages you already appear for but do not rank on page one for, then check whether the page answers that query clearly.",
      whyNowLabel: "Why now",
      whyNow: "The page already earns impressions, so an on-page improvement can be measured against a position that already exists.",
      potentialLabel: "What we would track",
      potential: "Impressions and average position for that query, before and after the change.",
    },
    footnote: "Illustrative example — not data from your site. Weekly items are built from the evidence SiteAgain collects and any search data you connect.",
  },

  // Pilot program value selection
  pilotSelection: {
    badge: "Pilot Program",
    headline: "Selective High-Value Pilot",
    detail: "We select pilot projects where our continuous growth formula brings clear, compounding business value. You get direct operator support and weekly data-driven search proposals.",
  },

  // Studio audit tab
  loadingAssessment: "Loading your assessment…",
  studioScanning: (host: string) => `Scanning ${host}. Findings appear here as soon as the audit completes.`,
  studioFailedTitle: "We could not finish the scan.",
  studioFailedDetail: (host: string) => `The audit of ${host} did not complete, so there is nothing verified to show. Rebuilding the homepage runs a fresh scan.`,

  // Sign-in
  auth: {
    privateTesting: "PRIVATE TESTING",
    welcome: "WELCOME",
    headline: ["Build your site again—", "without starting from zero."],
    inviteOnly: "This workspace is currently invite-only.",
    startFrom: "Start from the site you already have.",
    getStarted: "GET STARTED",
    welcomeBack: "WELCOME BACK",
    continueWithEmail: "Continue with email",
    signIn: "Sign in",
    email: "Email",
    invalidLink: "This sign-in link is invalid or expired.",
    sendFailed: "The sign-in link could not be sent",
    checkInbox: "Check your inbox. Your secure sign-in link expires in 10 minutes.",
    sending: "Sending…",
    sendLink: "Email me a sign-in link",
    back: "Back to my assessment",
  },
};

export type AssessmentCopy = typeof en;

const fr: AssessmentCopy = {
  languageLabel: "Langue",
  signIn: "Se connecter",

  kicker: "Vérification du site",
  heroLead: "Ce que nous avons constaté sur",
  heroLede: "Ce que voient les acheteurs, Google et les assistants IA aujourd'hui — mesuré, pas deviné.",
  source: "Site vérifié",
  method: "Méthode",
  methodSebora: "Vérification complète",
  methodLocal: "Page d'accueil uniquement",
  captured: "Capturée le",
  validUntil: "Valable jusqu'au",
  partial: "Quelques vérifications n'ont pas pu être faites.",
  partialLink: "Voir lesquelles",
  reportLanguage: (language: string) => `Les constats ont été rédigés en ${language} ; l'interface suit votre choix de langue.`,
  languageName: { en: "anglais", fr: "français" },

  nav: { verdict: "La réponse", buyers: "Ce que voient les acheteurs", foundation: "Détail technique", opportunities: "À corriger", evidence: "Preuves", action: "Plan", pilot: "Prochaine étape", limits: "Limites" },
  reportSections: "Sections du rapport",

  verdictLabel: "La réponse courte",
  verdict: {
    supported: { headline: "Votre site est prêt à être reconstruit en mieux.", detail: "Les preuves suffisent pour construire un aperçu privé de votre page d'accueil." },
    review_required: { headline: "Presque prêt — une courte vérification d'abord.", detail: "Nous pouvons avancer, mais une personne doit vérifier les points ci-dessous avant de construire l'aperçu." },
    unsupported: { headline: "Nous ne pouvons pas encore reconstruire ce site automatiquement.", detail: "Un obstacle empêche une reconstruction automatisée fiable. Les raisons sont listées ci-dessous." },
  },
  whyLabel: "Pourquoi",
  riskLabel: "Risque de reconstruction",
  risk: { low: "Faible", medium: "Moyen", high: "Élevé" },
  soWhat: {
    title: "Ce que cela signifie",
    topOpportunity: "Première priorité",
    noRedesignLabel: "Sans refonte",
    noRedesign: (fixable: number, total: number) => `Constat${total === 1 ? "" : "s"} testables dans un aperçu privé : ${fixable} sur ${total}. Aucune refonte nécessaire.`,
    noRedesignNone: "Aucun constat ici n'appelle de refonte.",
  },

  buyers: {
    title: "Ce que voient les acheteurs aujourd'hui",
    lede: "Nous avons lancé les recherches que font vos acheteurs, posé leurs questions aux assistants, chargé votre page d'accueil dans un vrai navigateur et vérifié les pages échantillonnées.",
    none: "Aucune vérification acheteur n'a été enregistrée pour ce rapport.",
    rankTitle: "Où vous vous situez sur Google",
    rankLede: "Les recherches exactes que nous avons lancées, et où votre site est apparu.",
    rankPosition: (position: number) => `n°${position} sur Google`,
    rankNotTop: (limit: number) => `Absent du top ${limit} capturé`,
    rankResultsTitle: "Qui obtient le clic",
    rankYou: "Vous",
    rankUnavailable: "Nous n'avons pas pu lire les résultats de recherche pour ce rapport.",
    aiTitle: "Les IA vous recommandent-elles ?",
      aiLede: "Nous avons posé à chaque assistant les questions que se posent vos acheteurs au moment de choisir un prestataire, et enregistré chaque réponse mot pour mot.",
    aiMentioned: "Vous mentionne",
    aiNotMentioned: "Ne vous mentionne pas",
    aiRecommended: "Vous recommande",
    aiNotRecommended: "Ne vous recommande pas",
    aiNamedLabel: "A aussi cité",
    aiUnavailable: "Aucun assistant IA n'a répondu pour ce rapport.",
    speedTitle: "Vitesse de chargement de votre page d'accueil",
    speedLede: "Mesurée une fois dans un vrai navigateur — pas une note de laboratoire.",
    speedSeconds: (seconds: number) => `${seconds} s`,
    speedGood: "Rapide",
    speedOk: "Acceptable",
    speedSlow: "Lent",
    speedDetail: (ttfbMs: number, requests: number) => `Première réponse en ${ttfbMs} ms · ${requests} requêtes`,
    speedUnavailable: "La vitesse n'a pas été mesurée pour ce rapport.",
    healthTitle: "Santé du site",
    healthLede: "Les bases attendues par les acheteurs et les moteurs de recherche, sur les pages vérifiées.",
    healthPages: (checked: number) => `${checked} page${checked === 1 ? "" : "s"} vérifiée${checked === 1 ? "" : "s"}`,
    healthClean: "Rien ne manque sur les pages vérifiées : titres, descriptions, titres H1 et textes alternatifs des images sont tous présents.",
    healthIssuesUnit: (affected: number, checked: number) => `${affected} sur ${checked}`,
    healthFixable: "Tout cela se corrige sans refonte.",
    healthIssue: {
      missing_meta_description: "pages sans méta-description",
      missing_h1: "pages sans titre H1",
      missing_title: "pages sans titre",
      images_without_alt: "images sans texte alternatif",
    } as Readonly<Record<string, string>>,
    unavailableNote: "Certaines vérifications n'ont pas pu être faites :",
    unavailableReason: {
      search_provider_unconfigured: "aucun fournisseur de recherche n'est connecté",
      search_query_failed: "une recherche a échoué",
      search_queries_unavailable: "les recherches acheteurs n'ont pas pu être préparées",
      ai_models_unconfigured: "aucun assistant IA n'est connecté",
      ai_visibility_unavailable: "aucun assistant IA n'a répondu",
      ai_assistant_unavailable: "un assistant IA n'a pas répondu",
      ai_prompts_unavailable: "les questions acheteurs n'ont pas pu être préparées",
      page_sample_unavailable: "aucun échantillon de page n'a été capturé",
      page_content_unavailable: "le contenu de cette page n'a pas pu être lu",
      speed_measurement_unconfigured: "aucun navigateur n'est connecté pour la vitesse",
      speed_measurement_unavailable: "la mesure de vitesse n'a produit aucun temps",
      speed_measurement_failed: "la mesure de vitesse a échoué",
    } as Readonly<Record<string, string>>,
    footnote: "Les recherches et les réponses IA ont été capturées pour ce rapport ; les deux évoluent avec le temps.",
  },

  foundationTitle: "Le détail technique",
  foundationLede: "Le regard de l'ingénieur sur votre site. Passez cette section si seuls les acheteurs vous intéressent.",
  bands: "Paliers : 0–44 faible · 45–74 à améliorer · 75–100 solide",
  scoresTitle: "Les notes",
  scoresLede: "Quatre mesures : le site est-il trouvable, citable par un assistant, rapide, et modifiable sans risque.",
  platformLabel: "Comment ce site est construit",
  platformDetected: (label: string) => `Détecté : ${label}`,
  platformContentInHtml: "Le contenu est présent dans le HTML.",
  platformContentInJs: "Le contenu est ajouté par JavaScript après le chargement.",
  dimensions: {
    seo: { label: "Recherche", question: "Les moteurs de recherche trouvent-ils et comprennent-ils cette page ?" },
    geo: { label: "Réponses IA", question: "Un assistant IA peut-il citer cette page correctement ?" },
    performance: { label: "Vitesse", question: "En combien de temps la page devient-elle utilisable sur mobile ?" },
    maintainability: { label: "Facile à mettre à jour", question: "La structure du site est-elle cohérente pour les modifications futures ?" },
  },
  publicEstimate: "Estimation publique",
  verifiedInPilot: "Vérifiée pendant le pilote",
  weakestSignals: "Signaux derrière le score",
  signalsToggle: "Ce qui explique cette note",
  componentLabels: {
    template_consistency: "Cohérence des gabarits",
    frontend_complexity: "Complexité front-end",
    markup_consistency: "Cohérence du balisage",
    architecture: "Architecture du site",
    content_structure: "Structure de contenu",
    dependency_burden: "Poids des dépendances",
    change_risk: "Risque de modification",
    delivery_model: "Mode de diffusion du contenu",
  } as Readonly<Record<string, string>>,
  componentBasis: {
    template_consistency: (i: Record<string, number>) =>
      `Similarité structurelle moyenne sur ${i.pairs ?? 0} paire${(i.pairs ?? 0) === 1 ? "" : "s"} de pages comparables.`,
    frontend_complexity: (i: Record<string, number>) =>
      `${i.scripts ?? 0} scripts (${i.thirdPartyScripts ?? 0} tiers), profondeur DOM ${i.domDepth ?? 0}, ${i.domNodes ?? 0} nœuds.`,
    markup_consistency: (i: Record<string, number>) =>
      `${i.inlineStyles ?? 0} styles en ligne, ${i.styleTags ?? 0} blocs style, écart de profondeur ${i.depthSpread ?? 0}.`,
    architecture: (i: Record<string, number>) =>
      `${i.minInternalLinks ?? 0} liens internes sur la page la plus pauvre, canonical sur ${i.canonicalPercent ?? 0}% des pages.`,
    content_structure: (i: Record<string, number>) =>
      `${i.present ?? 0} des ${i.checks ?? 0} motifs de métadonnées présents sur les pages échantillonnées.`,
    dependency_burden: (i: Record<string, number>) =>
      `${i.thirdPartyDomains ?? 0} domaines tiers, ${i.thirdPartyScripts ?? 0} scripts tiers.`,
    delivery_model: (i: Record<string, number>) => {
      switch (i.deliveryClass) {
        case 3: return "HTML fini : tout ce qui lit la page voit le contenu sans exécuter de script.";
        case 2: return "Assemblé à chaque requête par la plateforme : les modifications dépendent de ses gabarits, extensions et offre.";
        case 1: return "HTML pré-rendu, mais la page reste assemblée par l'application dans le navigateur.";
        default: return "Le contenu est ajouté par JavaScript : un lecteur qui n'exécute pas de script reçoit une page vide.";
      }
    },
    change_risk: (i: Record<string, number>) =>
      `Canonical ${i.canonicalPercent ?? 0}% · description ${i.descriptionPercent ?? 0}% · données structurées ${i.schemaPercent ?? 0}% présentes.`,
  } as Readonly<Record<string, (inputs: Record<string, number>) => string>>,
  status: {
    strong: "Solide",
    opportunity: "À améliorer",
    limited: "Faible",
    not_assessed: "Non évalué",
  },
  notScored: "Pas de note",
  confidence: "Confiance",
  confidenceLevel: { high: "élevée", medium: "moyenne", low: "faible", insufficient: "insuffisante" },

  opportunitiesTitle: "À corriger",
  opportunitiesCount: (count: number) => count === 0 ? "Rien à corriger" : `${count} correction${count === 1 ? "" : "s"}`,
  opportunitiesLede: "Ouvrez une correction pour voir pourquoi c'est important et ce que nous changerions.",
  opportunitiesEmpty: "Cette vérification n'a rien trouvé à corriger. Cela décrit ce que nous avons pu vérifier, pas la preuve que le site est sans défaut — la section Limites précise ce qui était hors périmètre.",
  filterAll: "Tout",
  filterEmpty: "Rien à ce niveau.",
  severity: { critical: "Critique", warning: "Alerte", opportunity: "Opportunité" },
  severityPlural: { critical: "Critiques", warning: "Alertes", opportunity: "Opportunités" },
  severityHint: {
    critical: "À corriger en premier",
    warning: "Freine la page",
    opportunity: "Utile à faire",
  },
  category: {
    qualification: "Éligibilité",
    seo: "Recherche",
    geo: "Réponses IA",
    performance: "Vitesse",
    maintainability: "Facile à mettre à jour",
  },
  observed: "Ce que nous avons vu",
  consequence: "Pourquoi c'est important",
  remediation: "Ce que nous changerions",
  technicalDetail: "Détail technique",
  directlyObserved: "Observé directement",
  likely: "Probable, non observé directement",
  pages: "Pages",
  morePages: (count: number) => `+${count} autre${count === 1 ? "" : "s"}`,
  evidence: (count: number) => `Preuves (${count})`,
  evidenceKind: {
    provider_observation: "observation du fournisseur",
    page_fact: "fait relevé sur la page",
    performance_metric: "mesure de performance",
  },
  safety: {
    safe_preview: "Testable dans un aperçu privé",
    observe: "Observation seulement",
    expert_review: "Revue d'expert nécessaire",
  },
  proposeFix: "Proposer cette modification",
  expand: "Voir le détail",
  collapse: "Masquer le détail",
  priority: { critical: "À faire d'abord", warning: "À faire ensuite", opportunity: "À tester" },
  signalLabel: "Signal attendu",
  signalLevel: { high: "Fort", medium: "Moyen", low: "Faible" },
  effortLabel: "Effort",
  effortLevel: { low: "Faible", medium: "Moyen", high: "Élevé" },

  evidenceTitle: "Preuves",
  evidenceLede: "D'où vient chaque correction. Ouvrez un élément à l'endroit où une correction le cite.",
  evidenceCount: (count: number) => count === 0 ? "Aucune preuve enregistrée" : `${count} preuve${count === 1 ? "" : "s"}`,
  evidenceEmpty: "Ce rapport ne cite aucune preuve. Cela décrit ce que l'analyse a enregistré, pas une absence de problèmes.",

  actionTitle: "La suite",
  actionLede: "Ce sur quoi porterait un premier aperçu, et comment chaque modification est vérifiée.",
  recommendedPage: "Page qui a le plus à gagner",
  homepageCaveat: "Le premier aperçu reconstruit tout de même votre page d'accueil, car elle devient la page d'entrée du nouveau site. C'est sur cette page que la migration complète rapporterait le plus.",
  proofPlanTitle: "Ce que l'aperçu démontrera",
  checkedBy: "Comment nous le vérifions",
  materialTitle: "Ce que nous avons changé, et pourquoi",
  changeType: {
    fidelity: "Fidélité",
    seo_optimization: "Recherche",
    geo_enhancement: "Réponses IA",
    performance_gain: "Vitesse",
    maintainability: "Maintenabilité",
  },

  limitsTitle: "Périmètre et limites",
  limitsDisclaimer: "Cette évaluation décrit ce que nous avons observé au moment de la capture. Elle ne prédit ni positionnement, ni trafic, ni citation par un assistant IA.",

  scanning: (host: string) => `Analyse de ${host} en cours : structure pour la recherche, lisibilité par l'IA, vitesse mobile et faisabilité de la migration.`,
  scanningDetail: "Cela prend en général moins d'une minute. Votre site en ligne n'est pas touché.",
  scanSteps: ["Signaux de recherche", "Lisibilité par l'IA", "Vitesse mobile", "Faisabilité de la migration"],
  scanFailed: (host: string) => `Nous n'avons pas pu terminer l'analyse de ${host}.`,
  scanFailedGeneric: "Nous n'avons pas pu terminer l'analyse de cette adresse.",
  scanFailedDetail: "L'adresse est peut-être injoignable, bloquée aux robots, ou n'est pas une page publique.",
  tryAnother: "Essayer une autre adresse",
  ctaTitle: "Envie que votre page d'accueil soit reconstruite sur ces preuves ?",
  ctaOpen: (host: string) => `Un compte lance un aperçu privé à partir de cette évaluation. Nous ne réanalyserons pas ${host}, et rien n'est publié sans votre validation.`,
  ctaInvite: "La construction de l'aperçu est sur invitation pendant la phase de test privé. Cette évaluation vous reste acquise dans tous les cas.",
  ctaRegister: "Signaler mon intérêt",
  ctaRegistered: "C'est noté. Nous ouvrons l'accès par ordre d'intérêt.",
  ctaCreate: "Créer un compte et reconstruire ma page d'accueil",
  ctaInviteSignIn: "J'ai une invitation — me connecter",

  // Portail de vérification email (le rapport complet n'apparaît qu'après
  // ouverture du lien envoyé par email)
  emailGate: {
    badge: "Rapport d'audit complet",
    unlockTitle: "Débloquez votre évaluation complète",
    unlockSubtitle: (host: string) => `Votre évaluation de ${host} est prête. Saisissez votre e-mail professionnel et nous vous enverrons un lien privé. Le rapport complet se débloque dès que votre e-mail est confirmé.`,
    emailLabel: "Adresse e-mail professionnelle",
    emailPlaceholder: "alex@entreprise.com",
    nameLabel: "Votre nom (optionnel)",
    namePlaceholder: "Alex Dupont",
    unlockButton: "Envoyez-moi le lien privé",
    submitting: "Envoi…",
    sentTitle: "Consultez votre boîte de réception",
    sentBody: (email: string, host: string) => `Nous analysons ${host}. Nous avons envoyé un lien de confirmation à ${email}. Votre rapport complet se débloquera dès que votre e-mail sera confirmé.`,
    resendButton: "Renvoyer le lien",
    resending: "Envoi…",
    resendDone: "Lien renvoyé. Consultez votre boîte de réception.",
    lockedNotice: "Le détail des constats, les preuves associées et le plan d'action restent verrouillés jusqu'à confirmation de votre e-mail. Aucun compte n'est nécessaire.",
    consentText: "J'accepte de recevoir mon rapport d'évaluation et les communications associées de SiteAgain.",
    scanPrompt: "Nous enverrons votre évaluation privée à cette adresse dès qu'elle sera prête :",
    scanButton: "Recevoir l'évaluation par e-mail",
    scanSaved: (email: string) => `Nous enverrons votre évaluation à ${email} dès la fin de l'analyse. Confirmez le lien reçu par e-mail pour débloquer le rapport complet.`,
    verified: "E-mail confirmé. Votre rapport est débloqué.",
  },

  // Transparence pour les sites performants (tous les scores évalués > 90)
  highScores: {
    badge: "Excellence Technique (Scores > 90)",
    headline: "Votre base technique est déjà excellente",
    detail: "Transparence totale : vous faites déjà un travail remarquable sur vos éléments techniques ! Votre site dispose d'une structure claire, d'une excellente réactivité mobile et de signaux de recherche solides. Vous n'avez pas besoin d'une refonte lourde ou d'une migration risquée. La vraie opportunité est de maintenir cet élan et d'accélérer votre croissance : protection continue contre les régressions et opportunités de recherche hebdomadaires basées sur votre Google Search Console.",
    growthHighlight: "Comment nous vous aidons à aller plus loin : surveillance continue pour préserver vos scores + recommandations hebdomadaires basées sur vos requêtes Google Search Console réelles et vos volumes de recherche.",
  },

  // Formule Growth Monitor à 59 € / mois
  growthMonitor: {
    badge: "Growth Monitor",
    price: "59 € / mois",
    priceNote: "par site · sans engagement",
    headline: "Votre base technique + une boucle de croissance hebdomadaire",
    summary: "Une surveillance technique continue de votre site, une liste courte de recommandations priorisées chaque semaine, et une mesure à 7 / 14 / 28 jours de ce qui a changé.",
    baseTitle: "Surveillance technique continue",
    baseDetail: "Vitesse, indexation et structure de recherche restent sous surveillance pour que les fondations ne bougent pas entre deux modifications.",
    recsTitle: "Recommandations priorisées chaque semaine",
    recsDetail: "Chaque semaine, les opportunités les plus importantes, classées à partir des preuves de recherche et techniques — à vous de décider.",
    implementsNote: "SiteAgain identifie, priorise et mesure le travail. C'est vous qui décidez quelles recommandations mettre en œuvre.",
    cta: "Lancer le suivi de croissance",
  },

  pilotTitle: "Pilote",
  pilotLede: "Si un pilote convient à ce site, et à quoi ressemblerait une croissance continue.",
  pilotUnavailable: {
    badge: "Pilote",
    headline: "L'éligibilité au pilote n'a pas été enregistrée pour ce rapport.",
    detail: "Ce rapport a été produit avant l'existence de la classification pilote : rien ici ne permet de dire si un pilote conviendrait. Nous ne le remplaçons pas par une supposition.",
  },
  pilotStrong: {
    badge: "Pilote — bon candidat",
    headline: "Ce site est un bon candidat pour un pilote.",
    detail: "Les constats ci-dessus sont concrets et mesurables, exactement ce qu'il faut à un pilote : un avant, un changement, un après comparable.",
  },
  pilotApplyFallback: "Les places de pilote sont limitées pendant la phase de test privé. Nous vous recontacterons à l'adresse e-mail utilisée pour ce rapport.",
  pilotApply: {
    title: "Candidater au pilote",
    lede: "Trois questions rapides. Nous examinons les candidatures manuellement, car nous n'acceptons que les sites pour lesquels nous pensons pouvoir créer une valeur mesurable.",
    horizonLabel: "Qu'est-ce qui compte le plus dans les 3 prochains mois ?",
    horizonPlaceholder: "Plus de demandes qualifiées depuis les pages de services",
    managerLabel: "Qui gère le site aujourd'hui ?",
    managerOptions: { me: "Moi", developer: "Un développeur", agency: "Une agence", nobody: "Personne actuellement" },
    gscLabel: "Pouvez-vous donner à SiteAgain un accès en lecture à Search Console ?",
    gscOptions: { yes: "Oui", no: "Pas encore" },
    capacityLabel: "Pouvez-vous mettre en œuvre environ 1 à 3 recommandations par semaine ?",
    capacityHint: "La plupart des recommandations sont des modifications de contenu ou de métadonnées, pas du code.",
    capacityOptions: { yes: "Oui", no: "Non" },
    submit: "Envoyer ma candidature",
    submitting: "Envoi…",
    doneTitle: "Candidature reçue",
    doneBody: (email: string) => `Nous examinons les candidatures manuellement. Nous reviendrons vers vous à ${email} avec une décision ; en cas d'acceptation, le pilote commence par la connexion à Search Console pour établir votre base de référence.`,
    error: "La candidature n'a pas pu être envoyée. Veuillez réessayer.",
  },
  pilotFoundation: {
    badge: "Pilote — plutôt croître",
    headline: "Ce site n'a pas besoin d'être réparé. Il a besoin de croître.",
    detail: "Les fondations sont solides et aucun constat n'appelle de refonte : un pilote de migration n'aurait rien à prouver ici. La suite utile est la boucle de croissance ci-dessous : surveiller la base technique et exploiter les opportunités de recherche qui apparaissent chaque semaine.",
  },
  pilotInsufficient: {
    badge: "Pilote — pas encore mesurable",
    headline: "Les preuves sont encore insuffisantes pour mesurer un pilote.",
    detail: "Un pilote n'a de sens que si l'on peut comparer un avant et un après. Ce rapport ne contient pas assez de preuves observables pour cela, et nous préférons le dire plutôt que promettre une mesure que nous ne pouvons pas faire.",
  },
  weeklySample: {
    badge: "Exemple",
    title: "Ce que vous recevriez la semaine prochaine",
    item: {
      title: "Une requête sur laquelle vous apparaissez déjà, sans être en première page",
      body: "Quand des données de recherche sont connectées, nous cherchons les pages sur lesquelles vous apparaissez déjà sans figurer en première page, puis nous vérifions si la page répond clairement à cette requête.",
      whyNowLabel: "Pourquoi maintenant",
      whyNow: "La page génère déjà des impressions : une amélioration sur la page se mesure donc par rapport à une position qui existe déjà.",
      potentialLabel: "Ce que nous suivrions",
      potential: "Les impressions et la position moyenne sur cette requête, avant et après la modification.",
    },
    footnote: "Exemple illustratif — pas des données de votre site. Les éléments hebdomadaires sont construits à partir des preuves que SiteAgain collecte et des données de recherche que vous connectez.",
  },

  // Sélection des projets pilotes
  pilotSelection: {
    badge: "Programme Pilote",
    headline: "Sélection de projets pilotes à forte valeur",
    detail: "Nous sélectionnons en priorité les projets pilotes auxquels notre formule de croissance continue apporte une valeur concrète et mesurable. Vous bénéficiez d'un accompagnement direct et de propositions de recherche hebdomadaires personnalisées.",
  },

  loadingAssessment: "Chargement de votre évaluation…",
  studioScanning: (host: string) => `Analyse de ${host} en cours. Les constats s'afficheront ici dès la fin de l'audit.`,
  studioFailedTitle: "Nous n'avons pas pu terminer l'analyse.",
  studioFailedDetail: (host: string) => `L'audit de ${host} ne s'est pas terminé, il n'y a donc rien de vérifié à afficher. Reconstruire la page d'accueil relance une analyse.`,

  auth: {
    privateTesting: "TEST PRIVÉ",
    welcome: "BIENVENUE",
    headline: ["Refaites votre site—", "sans repartir de zéro."],
    inviteOnly: "Cet espace est actuellement sur invitation.",
    startFrom: "Partez du site que vous avez déjà.",
    getStarted: "COMMENCER",
    welcomeBack: "BON RETOUR",
    continueWithEmail: "Continuer avec votre e-mail",
    signIn: "Se connecter",
    email: "E-mail",
    invalidLink: "Ce lien de connexion est invalide ou a expiré.",
    sendFailed: "Le lien de connexion n'a pas pu être envoyé",
    checkInbox: "Consultez votre boîte de réception. Votre lien de connexion sécurisé expire dans 10 minutes.",
    sending: "Envoi…",
    sendLink: "M'envoyer un lien de connexion",
    back: "Revenir à mon évaluation",
  },
};

const COPY: Record<Locale, AssessmentCopy> = { en, fr };

export function assessmentCopy(locale: Locale): AssessmentCopy {
  return COPY[locale];
}
